/**
 * Shared UI component for UI Select.
 * PARENT-CHILD CONTRACT: parent screens provide the data and configuration that
 * this wrapper renders or normalizes for reuse across the application.
 */
import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { OverlayOptions } from 'primeng/api';
import { MultiSelect, MultiSelectModule } from 'primeng/multiselect';
import { Select, SelectModule } from 'primeng/select';
import { fromEvent } from 'rxjs';

import { UiSelectOption } from './ui-select.types';
import { UiLabelTranslatePipe } from '../ui-label-translate.pipe';
import { resolveTranslatedUiLabel } from '../ui-label-translate.util';

const CLOSE_OTHERS_EVENT = 'ui-select:close-others';

interface UiPrimeSelectOption extends UiSelectOption {
  translatedLabel: string;
}

@Component({
  selector: 'ui-select',
  standalone: true,
  imports: [FormsModule, MultiSelectModule, SelectModule, UiLabelTranslatePipe],
  templateUrl: './ui-select.component.html',
  styleUrl: './ui-select.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiSelectComponent {
  private readonly translate = inject(TranslateService);
  private readonly languageVersion = signal(0);
  readonly selectRef = viewChild<Select>('selectRef');
  readonly multiSelectRef = viewChild<MultiSelect>('multiSelectRef');

  readonly label = input('');
  readonly id = input('');
  readonly value = input<string>('');
  readonly values = input<string[]>([]);
  readonly placeholder = input('Select option');
  readonly searchable = input(false);
  readonly searchPlaceholder = input('Search');
  readonly showPlaceholderOption = input(true);
  readonly options = input<UiSelectOption[]>([]);
  readonly disabled = input(false);
  readonly required = input(false);
  readonly validateRequired = input(true);
  readonly multiple = input(false);
  readonly closeOnSelect = input(false);
  readonly toggleOnTriggerClick = input(false);
  readonly loadMoreOnScroll = input(false);
  readonly loadingMoreOptions = input(false);
  readonly hasMoreOptions = input(false);
  readonly virtualScrollItemSize = input(40);
  readonly error = input('');
  readonly hint = input('');
  readonly valueChange = output<string>();
  readonly valuesChange = output<string[]>();
  readonly loadMoreOptions = output<void>();
  readonly searchChange = output<string>();
  /** PrimeNG overlay settings keep dropdown layering high without deprecated z-index inputs. */
  readonly overlayOptions: OverlayOptions = { autoZIndex: true, baseZIndex: 3000 };
  readonly resolvedOptions = computed<UiPrimeSelectOption[]>(() => {
    this.languageVersion();

    return this.options().map((option) => ({
      ...option,
      translatedLabel: this.resolveLabel(option.label)
    }));
  });

  constructor() {
    this.translate.onLangChange.pipe(takeUntilDestroyed()).subscribe(() => {
      this.languageVersion.update((version) => version + 1);
    });

    fromEvent<CustomEvent>(document, CLOSE_OTHERS_EVENT)
      .pipe(takeUntilDestroyed())
      .subscribe((event) => {
        if (event.detail !== this) {
          this.selectRef()?.hide();
          this.multiSelectRef()?.hide();
        }
      });
  }

  onShow(): void {
    document.dispatchEvent(new CustomEvent(CLOSE_OTHERS_EVENT, { detail: this }));
  }

  /**
   * PARENT-CHILD CONTRACT: paged dropdown parents own API page state.
   * The shared wrapper only reports when PrimeNG's virtual scroller reaches the
   * currently loaded edge so parents can append the next page.
   */
  onLazyLoad(event: { first?: number; last?: number }): void {
    if (!this.loadMoreOnScroll() || this.loadingMoreOptions() || !this.hasMoreOptions()) {
      return;
    }

    const loadedCount = this.resolvedOptions().length;
    const lastVisibleIndex = event.last ?? 0;

    if (loadedCount > 0 && lastVisibleIndex >= loadedCount - 2) {
      this.loadMoreOptions.emit();
    }
  }

  onValueChange(value: string | null | undefined): void {
    this.valueChange.emit(value ?? '');
  }

  onValuesChange(values: string[] | null | undefined): void {
    this.valuesChange.emit(values ?? []);

    if (this.closeOnSelect()) {
      this.multiSelectRef()?.hide();
    }
  }

  onSearchChange(event: { filter?: string }): void {
    this.searchChange.emit(event.filter ?? '');
  }

  onMultiSelectClick(): void {
    if (!this.toggleOnTriggerClick()) {
      return;
    }

    const multiSelect = this.multiSelectRef();
    if (multiSelect?.overlayVisible) {
      multiSelect.hide();
    }
  }

  private resolveLabel(value: string): string {
    return resolveTranslatedUiLabel(this.translate, value);
  }
}
